/**
 * T130 — Supplier API client + hooks.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { supplierKeys } from './keys';
import { useActiveTenantId } from '../../hooks/useTenantContext';
import type {
  CreateSupplierInput,
  UpdateSupplierInput,
  SupplierDto,
} from '@armard/shared';

interface PaginatedSuppliers {
  items: SupplierDto[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface SupplierListQueryInput {
  page?: number;
  limit?: number;
  q?: string;
  includeArchived?: boolean;
}

function buildQuery(q: SupplierListQueryInput): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const suppliersApi = {
  list: (q: SupplierListQueryInput = {}) =>
    api.get<PaginatedSuppliers>(`/suppliers${buildQuery(q)}`),
  get: (id: string) => api.get<SupplierDto>(`/suppliers/${id}`),
  create: (input: CreateSupplierInput) => api.post<SupplierDto>('/suppliers', input),
  update: (id: string, input: UpdateSupplierInput) =>
    api.put<SupplierDto>(`/suppliers/${id}`, input),
  archive: (id: string, version: number) =>
    api.post<SupplierDto>(`/suppliers/${id}/archive`, { version }),
  unarchive: (id: string, version: number) =>
    api.post<SupplierDto>(`/suppliers/${id}/unarchive`, { version }),
};

export function useSuppliers(query: SupplierListQueryInput = {}) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: supplierKeys.list(tenantId, query),
    queryFn: () => suppliersApi.list(query),
    enabled: tenantId !== null,
  });
}

export function useSupplier(id: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: supplierKeys.detail(tenantId, id!),
    queryFn: () => suppliersApi.get(id!),
    enabled: !!id && tenantId !== null,
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (input: CreateSupplierInput) => suppliersApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: supplierKeys.all(tenantId) }),
  });
}

export function useUpdateSupplier(id: string) {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (input: UpdateSupplierInput) => suppliersApi.update(id, input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: supplierKeys.all(tenantId) });
      qc.setQueryData(supplierKeys.detail(tenantId, id), data);
    },
  });
}

// T-G5-fe (Phase-17, FR-G5) — orthogonal archive-toggle hooks.
export function useArchiveSupplier(id: string) {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (version: number) => suppliersApi.archive(id, version),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: supplierKeys.all(tenantId) });
      qc.setQueryData(supplierKeys.detail(tenantId, id), data);
    },
  });
}

export function useUnarchiveSupplier(id: string) {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (version: number) => suppliersApi.unarchive(id, version),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: supplierKeys.all(tenantId) });
      qc.setQueryData(supplierKeys.detail(tenantId, id), data);
    },
  });
}
